import { initializeApp } from 'firebase/app'
import {
  getAuth,
  GoogleAuthProvider,
  TwitterAuthProvider,
  FacebookAuthProvider,
  OAuthProvider,
  signInWithEmailAndPassword,
  updatePassword,
} from 'firebase/auth'
import jwtDecode from 'jwt-decode'
import type { IUser, Auth } from 'oscar-typedef'
import { defineStore } from 'pinia'
import { ref, computed, type Ref } from 'vue'
import * as Sentry from '@sentry/vue'
import { useSetupStoreReset } from '@/composables'
import {
  FirebaseConfig,
  identityURL,
  WEPIN_TOKEN,
  SERVICE_NOTICE_SHOWN_KEY,
} from '@/const'
import { User2FAState } from '@/const/otpConfig'
import { FirebaseEmailSigninError } from '@/erros/FirebaseEmailSigninError'
import WepinBackendAPI from '@/networks/api/wepin/WepinBackendAPI'
import { type Maybe, getOAuthProcessor } from '@/types'
import {
  setLocalStorage,
  getLocalStorage,
  isExpiredRefreshToken,
} from '@/utils'

export type DecodedTokenType = {
  name: string
  email: string
  picture: string
}

type TypeProviderMapper = {
  Google: GoogleAuthProvider
  Twitter: TwitterAuthProvider
  Facebook: FacebookAuthProvider
  Apple: OAuthProvider
}

const useUserAPIStore = defineStore('userAPI', () => {
  const b64PubKey: Ref<string | undefined> = ref()
  const decodedUserToken = ref<Maybe<DecodedTokenType>>()
  const userInfo = ref<Maybe<IUser>>()

  const getPubKey = computed(() => {
    return b64PubKey.value
  })

  const app = initializeApp(FirebaseConfig)
  const auth = getAuth(app)

  async function getFBAccessToken() {
    if (!auth.currentUser) throw new Error('User is null')
    return auth.currentUser.getIdToken()
  }

  async function loginWepinWithToken(providerToken: string) {
    const response = await WepinBackendAPI.getInstance().user.login({
      idToken: providerToken,
    })
    setLocalStorage({ key: WEPIN_TOKEN, value: response.token })
    // 새 로그인 성공 = 이번 로그인에서 서비스 종료 안내를 다시 보여준다 (모든 로그인 경로가 이 함수를 거친다)
    localStorage.removeItem(SERVICE_NOTICE_SHOWN_KEY)
    return response
  }

  async function userCheckEmailExist(email: string) {
    return WepinBackendAPI.getInstance().user.checkEmailExist({
      email: email,
    })
  }

  async function userVerify(
    type: string,
    email: string,
    localeId?: number,
    promotionId?: string,
    campaignId?: string,
    referralCode?: string,
  ) {
    return WepinBackendAPI.getInstance().user.verify({
      type,
      email,
      localeId,
      promotionId,
      campaignId,
      referralCode,
    })
  }

  function isLoginUser() {
    const refreshToken = getLocalStorage()?.refreshToken
    return !(!refreshToken || isExpiredRefreshToken())
  }

  async function getUserDetail() {
    if (!isLoginUser()) return null
    userInfo.value = await WepinBackendAPI.getInstance().user.getInfo()
    return userInfo.value
  }

  async function logout() {
    const response = await WepinBackendAPI.getInstance().user.logout()
    useSetupStoreReset()
    setLocalStorage({ key: WEPIN_TOKEN, value: {} })
    // 다음 로그인 때 서비스 종료 안내가 다시 뜨도록 초기화
    localStorage.removeItem(SERVICE_NOTICE_SHOWN_KEY)
    // Sentry 사용자 정보 초기화
    Sentry.setUser(null)
    return response
  }
  async function getUserPubKey(): Promise<{ pubKey: string }> {
    const data = await WepinBackendAPI.getInstance().user.getPubKey()
    return data
  }
  async function updateLocale(localeId: number) {
    const data = await WepinBackendAPI.getInstance().user.updateLocale({
      localeId: localeId,
    })
    if (userInfo.value) userInfo.value.localeId = data.localeId
    return data
  }
  async function updateUser({
    localeId = userInfo.value?.localeId,
    name,
    currencyId,
    userJoinStage,
  }: {
    localeId?: number
    name?: string
    currencyId?: number
    userJoinStage?: number
  }) {
    const data = await WepinBackendAPI.getInstance().user.updateUser({
      localeId,
      name,
      currencyId,
      userJoinStage,
    })
    // 기존 userInfo 값을 유지하면서 업데이트된 필드만 병합
    if (userInfo.value) {
      userInfo.value = { ...userInfo.value, ...data }
    } else {
      userInfo.value = data
    }
    return data
  }
  async function updateUserProfileImage(files: any) {
    const data =
      await WepinBackendAPI.getInstance().user.updateUserProfileImage(files)
    if (userInfo.value) {
      userInfo.value.profileImage = data.filePath as string
    }
    return data
  }
  async function refreshAccessToken() {
    const data = await WepinBackendAPI.getInstance().user.refreshAccessToken()
    const value = {
      access: data.token,
      refresh: getLocalStorage().refreshToken,
    }

    setLocalStorage({ key: WEPIN_TOKEN, value })
    return
  }
  async function updateTermsAccepted() {
    const data = await WepinBackendAPI.getInstance().user.updateTermsAccepted({
      termsAccepted: {
        termsOfService: true,
        privacyPolicy: true,
      },
    })
    return data
  }
  async function getTermsAccepted() {
    const data = await WepinBackendAPI.getInstance().user.getTermsAccepted()
    return data
  }
  async function getUserPasswordState(email: string) {
    const data = await WepinBackendAPI.getInstance().user.getUserPasswordState({
      email,
    })
    return data
  }
  async function updateUserPasswordState(isPasswordResetRequired: boolean) {
    const data =
      await WepinBackendAPI.getInstance().user.updateUserPasswordState({
        isPasswordResetRequired,
      })
    return data
  }
  async function signInOAuth(
    providerString: string,
    customParameter?: Record<string, unknown>,
  ) {
    // V1 방식 유지: OAuthProcessor 사용
    // V2 백엔드 API만 변경 (getOauthProviderInfoList → V2 API 호출)
    const processor = await getOAuthProcessor(auth, providerString)
    if (!processor) {
      throw new Error('Invalid provider')
    }
    if (customParameter) {
      processor.setCustomParameters(customParameter)
    }
    const token = await processor.doPopupLogin()

    return afterSignIn(token)
  }

  async function afterSignIn(token: string) {
    decodedUserToken.value = jwtDecode(token)
    const result = await loginWepinWithToken(token)
    userInfo.value = result.userInfo

    // Sentry 사용자 정보 설정
    if (result.userInfo) {
      Sentry.setUser({
        id: result.userInfo.userId,
        email: result.userInfo.email,
      })
    }

    return result
  }

  async function resetPassword({
    oobCode,
    password,
  }: {
    oobCode: string
    password: string
  }) {
    try {
      const result = await fetch(identityURL.resetPassword, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          oobCode: oobCode,
          newPassword: password,
        }),
      })
      if (result.status >= 400) {
        throw new Error('Expired token')
      }
      return result
    } catch (error) {
      throw error
    }
  }

  async function confirmEmailVerify({ oobCode }: { oobCode: string }) {
    const result = await fetch(identityURL.updateVerifyEmail, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        oobCode,
      }),
    })
    return result
  }

  async function updateUserPassword(password: string) {
    const app = initializeApp(FirebaseConfig)
    const auth = getAuth(app)
    if (!auth.currentUser) return
    const res = await updatePassword(auth.currentUser, password)
      .then(async () => true)
      .catch((error) => {
        throw error
      })
    return res
  }

  async function signInWithEmail({
    email,
    password,
  }: {
    email: string
    password: string
  }) {
    const app = initializeApp(FirebaseConfig)
    const auth = getAuth(app)
    const res = await signInWithEmailAndPassword(auth, email, password)
      .then(async (userCredential) => {
        if (!userCredential.user.emailVerified) {
          throw new Error('require/email-verified')
        }
        const token = await userCredential.user.getIdToken()

        decodedUserToken.value = jwtDecode(token)
        const result = await loginWepinWithToken(token)
        userInfo.value = result.userInfo

        // Sentry 사용자 정보 설정
        if (result.userInfo) {
          Sentry.setUser({
            id: result.userInfo.userId,
            email: result.userInfo.email,
          })
        }

        return result
      })
      .catch((error) => {
        switch (error.code) {
          case 'auth/too-many-requests':
            throw new FirebaseEmailSigninError('too-many-requests')
          case 'auth/wrong-password':
            throw new FirebaseEmailSigninError('wrong-password')
          default:
            throw { error: error }
        }
      })
    return res
  }
  const reset = () => {
    b64PubKey.value = ''
    decodedUserToken.value = undefined
    userInfo.value = undefined
  }

  const otpVerify = async (codeData: Auth.VerifyCode.Request.Body) => {
    return WepinBackendAPI.getInstance()
      .user.verifyOtpCode(codeData)
      .then((result: any) => {
        // V2 API: verified 필드 사용
        if (result.verified && userInfo.value) {
          if (userInfo.value.use2FA < User2FAState.AUTHENTICATED) {
            userInfo.value.use2FA = User2FAState.AUTHENTICATED
          }
        }
        return result
      })
  }

  const getRecoveryCode = async (
    codeData: Auth.GetRecoveryCode.Request.Queries,
  ) => {
    return WepinBackendAPI.getInstance()
      .user.getRecoveryCode(codeData)
      .then((result: any) => {
        if (userInfo.value) {
          userInfo.value.use2FA = User2FAState.RECOVERY_CHECKED
        }
        return result.recoveryCode
      })
  }

  const unsetOtp = async (codeData: Auth.Unset.Request.Queries) => {
    return WepinBackendAPI.getInstance()
      .user.unSetOtp(codeData)
      .then(({ result }: { result: boolean }) => {
        if (result) {
          if (userInfo.value) {
            userInfo.value.use2FA = User2FAState.UNSET
          }
        }
        return result
      })
  }

  const refreshRecoveryCode = async (
    codeData: Auth.UpdateRecoveryCode.Request.Body,
  ) => {
    return WepinBackendAPI.getInstance()
      .user.refreshOtpRecoveryCode(codeData)
      .then((result: any) => {
        return result.recoveryCode
      })
  }

  const change2FAState = async (state: number) => {
    return WepinBackendAPI.getInstance()
      .user.change2FAState(state)
      .then(({ result }: { result: boolean }) => {
        if (result) {
          if (userInfo.value) {
            userInfo.value.use2FA = state
          }
        }
        return result
      })
  }

  return {
    getFBAccessToken,
    signInOAuth,
    signInWithEmail,
    logout,
    getUserDetail,
    getPubKey,
    userInfo,
    decodedUserToken,
    getUserPubKey,
    updateLocale,
    refreshAccessToken,
    updateTermsAccepted,
    getTermsAccepted,
    getUserPasswordState,
    updateUserPasswordState,
    loginWepinWithToken,
    userCheckEmailExist,
    userVerify,
    resetPassword,
    confirmEmailVerify,
    updateUser,
    updateUserProfileImage,
    updateUserPassword,
    reset,
    otpVerify,
    getRecoveryCode,
    unsetOtp,
    refreshRecoveryCode,
    change2FAState,
    isLoginUser,
  }
})

export { useUserAPIStore }
export type { TypeProviderMapper }
